import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { 
  Zap, Leaf, Globe, Users, Target, Heart, 
  ArrowRight, CheckCircle2, Building2
} from 'lucide-react';

const AboutPageEN = () => {
  const values = [
    {
      icon: Leaf,
      title: 'Sustainability',
      description: 'We are committed to a greener future by promoting electric mobility and clean energy.'
    },
    {
      icon: Target,
      title: 'Innovation',
      description: 'We continuously invest in new technologies to offer the best charging experience.'
    },
    {
      icon: Users,
      title: 'Reliability',
      description: 'We build long-term relationships with our customers through reliable and transparent service.'
    },
    {
      icon: Heart,
      title: 'Commitment',
      description: 'Every decision we make focuses on the needs of our users and the well-being of the community.'
    }
  ];

  const milestones = [
    { year: '2020', title: 'Foundation', desc: 'Our mission for sustainable mobility began' },
    { year: '2021', title: 'First Station', desc: 'The first station opens in Tirana' },
    { year: '2022', title: 'National Expansion', desc: 'Network extends to 5 cities' },
    { year: '2023', title: 'Ultra-Fast', desc: 'Installation of 240 kW chargers' },
    { year: '2024', title: 'Mobile App', desc: 'Launch of the mobile application' }
  ];

  return (
    <div data-testid="about-page-en" className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="section-padding bg-slate-50">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="inline-block text-sm font-medium uppercase tracking-widest text-blue-600 mb-4">
                About Us
              </span>
              <h1 
                data-testid="about-title"
                className="text-4xl md:text-6xl font-bold text-slate-900 font-['Inter_Tight'] tracking-tight mb-6"
              >
                Energy for a<br />
                <span className="text-blue-600">Greener Albania</span>
              </h1>
              <p className="text-lg text-slate-500 leading-relaxed mb-8">
                AlbMove is the leading charging network operator for electric vehicles in Albania. 
                Our mission is to make electric charging simple, affordable, and accessible for everyone.
              </p>
              <div className="flex items-center gap-8">
                <div>
                  <p className="text-4xl font-bold text-blue-600 font-['Inter_Tight']">8+</p>
                  <p className="text-slate-500 text-sm">Cities</p>
                </div>
                <div className="w-px h-12 bg-slate-200" />
                <div>
                  <p className="text-4xl font-bold text-blue-600 font-['Inter_Tight']">25+</p>
                  <p className="text-slate-500 text-sm">Chargers</p>
                </div>
                <div className="w-px h-12 bg-slate-200" />
                <div>
                  <p className="text-4xl font-bold text-blue-600 font-['Inter_Tight']">1250+</p>
                  <p className="text-slate-500 text-sm">MWh</p>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1760201938689-a2b0f52bbcd9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwzfHxlbGVjdHJpYyUyMGNhciUyMGRyaXZpbmclMjBzY2VuaWMlMjByb2FkJTIwYWxiYW5pYSUyMG5hdHVyZXxlbnwwfHx8fDE3Njk3MzgyMTl8MA&ixlib=rb-4.1.0&q=85"
                alt="Berat, Albania"
                className="rounded-3xl shadow-lg"
                data-testid="about-hero-image"
              />
              <div className="absolute -bottom-4 -left-4 bg-lime-400 text-slate-900 rounded-2xl px-6 py-4">
                <p className="font-bold font-['Inter_Tight']">100%</p>
                <p className="text-sm">Clean Energy</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-blue-100 mb-6">
              <Globe className="w-8 h-8 text-blue-600" />
            </div>
            <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 font-['Inter_Tight'] mb-6">
              Our Mission
            </h2>
            <p className="text-xl text-slate-500 leading-relaxed">
              To build the infrastructure needed to support Albania's transition towards 
              electric mobility, offering reliable, affordable, and environmentally 
              friendly charging services.
            </p>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section data-testid="values-section" className="section-padding bg-slate-50">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="inline-block text-sm font-medium uppercase tracking-widest text-blue-600 mb-4">
              Our Values
            </span>
            <h2 className="text-4xl md:text-5xl font-semibold text-slate-900 font-['Inter_Tight'] tracking-tight">
              What Guides Us
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div 
                key={index}
                data-testid={`value-card-${index}`}
                className="bg-white p-8 rounded-3xl border border-slate-200/50 text-center hover:border-blue-200 hover:shadow-lg transition-all"
              >
                <div className="w-16 h-16 rounded-2xl bg-blue-100 flex items-center justify-center mx-auto mb-6">
                  <value.icon className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 font-['Inter_Tight'] mb-3">
                  {value.title}
                </h3>
                <p className="text-slate-500 leading-relaxed text-sm">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section data-testid="timeline-section" className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="inline-block text-sm font-medium uppercase tracking-widest text-blue-600 mb-4">
              Our History
            </span>
            <h2 className="text-4xl md:text-5xl font-semibold text-slate-900 font-['Inter_Tight'] tracking-tight">
              The AlbMove Journey
            </h2>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <div className="relative">
              {/* Timeline Line */}
              <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-0.5 bg-blue-200 transform md:-translate-x-1/2" />
              
              {milestones.map((milestone, index) => (
                <div 
                  key={index}
                  data-testid={`milestone-${index}`}
                  className={`relative flex items-center gap-8 mb-12 ${
                    index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}
                >
                  {/* Dot */}
                  <div className="absolute left-8 md:left-1/2 w-4 h-4 rounded-full bg-blue-500 border-4 border-white shadow transform -translate-x-1/2" />
                  
                  {/* Content */}
                  <div className={`ml-20 md:ml-0 md:w-1/2 ${index % 2 === 0 ? 'md:pr-16 md:text-right' : 'md:pl-16'}`}>
                    <span className="inline-block px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-2">
                      {milestone.year}
                    </span>
                    <h3 className="text-xl font-semibold text-slate-900 font-['Inter_Tight'] mb-1">
                      {milestone.title}
                    </h3>
                    <p className="text-slate-500">{milestone.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section-padding text-white" style={{ background: 'linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)' }}>
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="inline-block text-sm font-medium uppercase tracking-widest text-lime-400 mb-4">
                Why AlbMove?
              </span>
              <h2 className="text-4xl md:text-5xl font-semibold font-['Inter_Tight'] tracking-tight mb-8">
                A Reliable Partner for Electric Mobility
              </h2>
              
              <ul className="space-y-4">
                {[
                  'The widest network in Albania',
                  'Advanced charging technology',
                  '24/7 customer support',
                  'Competitive and transparent pricing',
                  'Customized business solutions',
                  'Commitment to clean energy'
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <CheckCircle2 className="w-6 h-6 text-lime-400 flex-shrink-0" />
                    <span className="text-blue-100">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="bg-blue-800/50 rounded-3xl p-8 md:p-12">
              <Building2 className="w-12 h-12 text-lime-400 mb-6" />
              <h3 className="text-2xl font-semibold font-['Inter_Tight'] mb-4">
                Business Solutions
              </h3>
              <p className="text-blue-200 leading-relaxed mb-6">
                We offer customized packages for companies that want to switch to an electric fleet. 
                From charger installation to complete consumption management.
              </p>
              <Button 
                asChild
                data-testid="business-cta-button"
                className="bg-lime-400 hover:bg-lime-300 text-slate-900 rounded-full"
              >
                <Link to="/en/contact">
                  Request a Quote
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-slate-50">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 font-['Inter_Tight'] mb-6">
            Want to Partner With Us?
          </h2>
          <p className="text-lg text-slate-500 mb-8 max-w-xl mx-auto">
            We're always open to new partnerships. Contact us to discuss opportunities.
          </p>
          <Button 
            asChild
            data-testid="contact-cta-button"
            className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-8 py-6 text-lg"
          >
            <Link to="/en/contact">
              Contact Us
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default AboutPageEN;
